import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../config/supabase';
import AddTestimonialModal from './modals/AddTestimonialModal';

function Process() {
    const steps = [
        { title: "Discovery", desc: "We analyze your requirements." },
        { title: "Design", desc: "Creating intuitive UI/UX prototypes." },
        { title: "Development", desc: "Writing clean, scalable code." },
        { title: "Launch", desc: "Rigorous testing and deployment." },
    ];

    const [reviews, setReviews] = useState([]);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const isOnline = navigator.onLine && isSupabaseConfigured;

    useEffect(() => {
        const initialData = [
            { name: "Yeri Israel Kombe", role: "Director, Tella Ent.", text: "Ezems Tech is the place to be. They transformed how we handle operations." },
            { name: "Sarah L.", role: "CEO, Innovate Co.", text: "The team delivered a fantastic website that exceeded our expectations." }
        ];

        if (isOnline) {
            const fetchReviews = async () => {
                const { data } = await supabase.from('testimonials').select('*').order('created_at', { ascending: false });
                if (data) setReviews([...initialData, ...data]);
                else setReviews(initialData);
            };
            fetchReviews();
        } else {
            const localReviews = JSON.parse(localStorage.getItem('ezems_local_testimonials') || '[]');
            setReviews([...initialData, ...localReviews]);
        }
    }, [isOnline]);

    return (
        <section id="process" className="py-16 md:py-24 bg-slate-900 text-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">Our Proven Process</h2>
                        <p className="text-slate-400 text-base md:text-lg mb-8">
                            Transparency from the first meeting to final deployment.
                        </p>
                        
                        <div className="space-y-6">
                            {steps.map((step, i) => (
                                <div key={i} className="flex gap-4">
                                    <div className="flex flex-col items-center">
                                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-sm">
                                            {i + 1}
                                        </div>
                                        {i !== steps.length - 1 && <div className="w-0.5 h-full bg-slate-800 mt-2"></div>}
                                    </div>
                                    <div className="pb-6">
                                        <h4 className="font-bold text-lg">{step.title}</h4>
                                        <p className="text-slate-400 text-sm mt-1">{step.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="bg-slate-800/50 backdrop-blur-sm p-6 md:p-8 rounded-3xl border border-slate-700 flex flex-col h-full max-h-[600px]">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl md:text-2xl font-bold">Client Testimonials</h3>
                            <button 
                                onClick={() => setIsReviewModalOpen(true)} 
                                className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg transition-colors font-bold"
                            >
                                <Plus size={14} /> Add Review
                            </button>
                        </div>
                        
                        <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1">
                            {reviews.map((t, i) => (
                                <div key={i} className="bg-slate-900 p-5 rounded-xl border border-slate-800 animate-fade-in">
                                    <div className="flex gap-1 text-yellow-500 mb-2 md:mb-3 text-xs">
                                        {[...Array(5)].map((_, j) => <span key={j}>★</span>)}
                                    </div>
                                    <p className="text-slate-300 italic mb-4 text-sm">"{t.text}"</p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
                                            {t.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-sm text-white">{t.name}</div>
                                            <div className="text-xs text-slate-500">{t.role}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <AddTestimonialModal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)} />
        </section>
    );
}

export default Process;